import { useCallback, useEffect, useMemo, useState } from 'react'
import Tabs from '#/components/Tabs'

const PDP_SECTIONS = [
  { id: 'valuations', label: 'Property Valuations' },
  { id: 'location', label: 'Location Information' },
  { id: 'lot_details', label: 'Lot Details' },
  { id: 'openhouse', label: 'Open House' },
  { id: 'tours', label: '3D & Video Tours' },
  { id: 'listing_agents', label: 'Listing Agents' },
  { id: 'property_details', label: 'Property Details' },
  { id: 'permits', label: 'Permits' },
  { id: 'ownership', label: 'Current Ownership' },
  { id: 'history', label: 'Property History' },
  { id: 'environment', label: 'Environment' },
  { id: 'school', label: 'School Information' },
  { id: 'comparables', label: 'Comparable Properties' },
  { id: 'arv_calculator', label: 'ARV Calculator' },
  { id: 'neighborhood', label: 'Neighborhood' },
  { id: 'nearby_statistics', label: 'Nearby Statistics' },
  { id: 'market', label: 'Market Trends' },
  { id: 'properties_nearby', label: 'Nearby Properties' }
]

type Props = {
  data: any
  isWindowScroll: boolean
  containerId?: string
}

/**
 * Navigation tabs to sections in PDP
 *
 * @prop data: whenever it changes, check existing sections
 */
const NavBar: React.FC<Props> = ({ data, isWindowScroll, containerId }) => {
  const [section, setSection] = useState('')
  const getContainer = useCallback(
    () =>
      (isWindowScroll ? window : document.getElementById(containerId as string)) as HTMLElement,
    [isWindowScroll, containerId]
  )

  const sections = useMemo(
    () => PDP_SECTIONS.filter(e => document.getElementById(e.id)),
    [data]
  )

  const handleScroll = useCallback(() => {
    const container = getContainer()
    const scrollY = isWindowScroll ? window.pageYOffset : container.scrollTop
    for (let i = 0; i < sections.length; ++i) {
      const ele = document.getElementById(sections[i].id)

      if (ele && ((ele.offsetTop - (isWindowScroll ? 150 : 100)) > scrollY)) {
        setSection(sections[i].id)
        break
      }
    }
  }, [getContainer, sections])

  useEffect(() => {
    if (typeof (window) !== 'undefined') {
      const container = getContainer()
      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [getContainer, handleScroll])

  const handleSelect = (_: React.SyntheticEvent<Element, Event>, value: string) => {
    const ele = document.getElementById(value)

    // 100 = overview_height(52 + tabbar_height(48)
    // 150 = 100 + page_header(50)
    const topPadding = 12
    if (ele) {
      const container = getContainer()
      container.scrollTo({
        top: ele.offsetTop - (isWindowScroll ? 150 : 100) - topPadding,
        left: 0,
        behavior: 'smooth'
      })
    }
  }

  return (
    <div className='z-30 border-b border-gray-200 bg-primary-bg'>
      <Tabs value={section || sections[0]?.id} onChange={handleSelect}>
        {sections.map(e => (
          <Tabs.Tab
            key={e.id}
            label={<span className='whitespace-nowrap'>{e.label}</span>}
            value={e.id}
          />
        ))}
      </Tabs>
    </div>
  )
}

export default NavBar
